// Scheduled-task output delivery orchestrator for the manager.
//
// deliverScheduledTaskOutput routes a completed scheduled task's output to its
// resolved target — a Slack/Telegram channel, or a DM resolved via
// @augmented/core/delivery — applying the attribution + schedule-edit-link
// footers and reporting delivery status. It composes the delivery leaf's lower
// layers (./state.ts, ./notifications.ts) and the channel senders
// (../channels/*); it depends on no scheduler or lifecycle module, so the
// scheduler subsystem can import it without a cycle. See ADR-0011.

import {
  parseDeliveryTarget,
  isParseError,
  resolveDmTarget,
  isResolveError,
  appendDmFooter,
} from '@augmented/core';
import { api } from '../../api-client.js';
import { log } from '../runtime.js';
import { withScheduleLinkFooter } from '../../delivery-schedule-link.js';
import { agentChannelTokens } from '../channels/state.js';
import { postSlackChannelMessage, sendSlackChannelMessage, maybePostSlackThreadHint } from '../channels/slack.js';
import { telegramApiCall, maybeSendTelegramFollowUpHint } from '../channels/telegram.js';
import { agentInfoForDelivery } from './state.js';
import { sendTaskNotification, reportDeliveryStatus, maybePostScheduledTaskRatingPrompt } from './notifications.js';

/** ENG-4422 §5–§6: deliver a scheduled task's output via a structured
 *  DeliveryTarget (post-JSONB-migration) or a legacy `channel:<id>` /
 *  `chat:<id>` opaque string (OpenClaw paths that still pre-date the
 *  migration consumer-side).
 *
 *  - Channel targets route via the existing Slack/Telegram send helpers.
 *  - DM targets call @augmented/core/delivery.resolveDmTarget using the
 *    agent's refresh payload (reports_to_slack_user_id / reports_to_person
 *    ids, populated by host-runtime — see §8), then dispatch.
 *  - Slack DMs open the IM via conversations.open and post with chat:write.
 *  - Every DM body gets the attribution footer (§6).
 *  - Failure status is posted to /host/schedules/delivery-status so the
 *    server can update the observability columns (§10) + emit a
 *    high-severity log line on 3+ consecutive failures.
 */
// Exported for unit testing (ENG-6038 threaded delivery + top-level
// fallback); production callers are all in this module.
export async function deliverScheduledTaskOutput(
  agentCodeName: string,
  agentId: string,
  rawTarget: unknown,
  body: string,
  taskId?: string,
): Promise<void> {
  // ENG-4462: helper — wrap body with the schedule-edit link footer for the
  // given medium, honouring per-agent/host env kill switches. No-op when
  // taskId is absent or footer is disabled.
  const withLink = (b: string, medium: 'slack' | 'telegram'): string =>
    withScheduleLinkFooter({ body: b, medium, codeName: agentCodeName, agentId, taskId, log });

  // Legacy string form (OpenClaw -> manager-worker path, and any still-on-wire
  // rows we haven't flushed). Parse and route via sendTaskNotification.
  if (typeof rawTarget === 'string') {
    if (rawTarget.startsWith('channel:')) {
      const result = await sendTaskNotification(agentCodeName, 'slack', rawTarget, withLink(body, 'slack'));
      await reportDeliveryStatus(agentId, taskId, {
        status: result.ok ? 'ok' : 'failed',
        medium: 'slack',
        error_code: result.ok ? null : (result.error_code ?? 'SLACK_SEND_FAILED'),
      });
      return;
    }
    if (rawTarget.startsWith('chat:')) {
      const result = await sendTaskNotification(agentCodeName, 'telegram', rawTarget, withLink(body, 'telegram'));
      await reportDeliveryStatus(agentId, taskId, {
        status: result.ok ? 'ok' : 'failed',
        medium: 'telegram',
        error_code: result.ok ? null : (result.error_code ?? 'TELEGRAM_SEND_FAILED'),
      });
      return;
    }
    // Unknown string prefix — drop + log.
    log(`[delivery] Unrecognised legacy delivery_to string for '${agentCodeName}': ${rawTarget.slice(0, 60)}`);
    await reportDeliveryStatus(agentId, taskId, { status: 'failed', error_code: 'LEGACY_DELIVERY_TO_UNRECOGNISED' });
    return;
  }

  const parsed = parseDeliveryTarget(rawTarget);
  if (isParseError(parsed)) {
    log(`[delivery] Malformed delivery_to for '${agentCodeName}': ${parsed.code} — ${parsed.detail}`);
    await reportDeliveryStatus(agentId, taskId, { status: 'failed', error_code: parsed.code });
    return;
  }

  if (parsed.kind === 'channel') {
    if (parsed.provider === 'slack') {
      const channelId = parsed.channel_id ?? '';
      // ENG-6038: announce back into the originating thread when the task
      // captured one. Absent thread_ts → top-level (pre-ENG-6038 behaviour).
      const threadTs = parsed.thread_ts;
      let sent = await postSlackChannelMessage(agentCodeName, channelId, withLink(body, 'slack'), threadTs);
      let deliveredInThread = Boolean(threadTs);
      if (!sent.ok && threadTs && (sent.error === 'thread_not_found' || sent.error === 'message_not_found')) {
        // Fail open: the originating thread's parent is gone (deleted /
        // purged). A days-later one-shot must still reach the user — retry
        // top-level rather than silently dropping the delivery.
        log(`[delivery] Originating thread ${threadTs} gone in ${channelId} for '${agentCodeName}' — falling back to top-level post`);
        sent = await postSlackChannelMessage(agentCodeName, channelId, withLink(body, 'slack'));
        deliveredInThread = false;
      }
      await reportDeliveryStatus(agentId, taskId, {
        status: sent.ok ? 'ok' : 'failed',
        medium: 'slack',
        // Preserve Slack's concrete failure code (channel_not_found,
        // not_in_channel, auth errors, …) in the observability row rather
        // than collapsing everything to the generic constant.
        error_code: sent.ok
          ? null
          : (sent.error ? `SLACK_SEND_FAILED:${sent.error}` : 'SLACK_SEND_FAILED'),
      });
      if (sent.ok) {
        // The "you can reply in this thread" discoverability hint is
        // redundant when the delivery already landed inside a thread.
        if (!deliveredInThread) {
          await maybePostSlackThreadHint(agentCodeName, channelId, sent.ts);
        }
        // ENG-4576 (PR #538): kick the API to post a threaded rating
        // prompt under the scheduled-task delivery. Server-side decides
        // whether to fire (block_kit_enabled, run outcome, idempotency).
        // Fire-and-forget — never block the delivery path on this.
        if (sent.ts && taskId) {
          await maybePostScheduledTaskRatingPrompt(agentId, taskId, channelId, sent.ts);
        }
      }
      return;
    }
    // Telegram channel-target
    const chatId = parsed.chat_id ?? '';
    const toStr = `chat:${chatId}`;
    const result = await sendTaskNotification(agentCodeName, 'telegram', toStr, withLink(body, 'telegram'));
    await reportDeliveryStatus(agentId, taskId, {
      status: result.ok ? 'ok' : 'failed',
      medium: 'telegram',
      error_code: result.ok ? null : (result.error_code ?? 'TELEGRAM_SEND_FAILED'),
    });
    if (result.ok) {
      const botToken = agentChannelTokens.get(agentCodeName)?.telegram;
      if (botToken) await maybeSendTelegramFollowUpHint(agentCodeName, botToken, chatId);
    }
    return;
  }

  // DM target — resolve person + medium, then dispatch with attribution footer.
  const agentRow = agentInfoForDelivery.get(agentCodeName);
  if (!agentRow) {
    log(`[delivery] No agent metadata cached for '${agentCodeName}' — dropping DM`);
    await reportDeliveryStatus(agentId, taskId, { status: 'failed', error_code: 'AGENT_METADATA_UNAVAILABLE' });
    return;
  }

  const resolved = resolveDmTarget(parsed, agentRow.resolverAgent, agentRow.peopleByPersonId);
  if (isResolveError(resolved)) {
    log(`[delivery] Cannot resolve DM target for '${agentCodeName}': ${resolved.code} — ${resolved.detail}`);
    await reportDeliveryStatus(agentId, taskId, { status: 'failed', error_code: resolved.code });
    return;
  }

  // Attribution footer first (§6), then the ENG-4462 schedule-edit link
  // underneath — order matters: the attribution identifies who sent the
  // message, the link lets the recipient act on it.
  const attributionBody = appendDmFooter(
    body,
    agentRow.ownerTeamName,
    agentRow.agentDisplayName,
  );
  const footeredBody =
    resolved.kind === 'dm'
      ? withLink(attributionBody, resolved.medium === 'slack' ? 'slack' : 'telegram')
      : attributionBody;

  if (resolved.kind === 'dm' && resolved.medium === 'slack') {
    const tokens = agentChannelTokens.get(agentCodeName);
    const botToken = tokens?.slack;
    if (!botToken) {
      await reportDeliveryStatus(agentId, taskId, { status: 'failed', error_code: 'SLACK_MISSING_SCOPE', medium: 'slack' });
      log(`[delivery] No Slack bot token for '${agentCodeName}' — DM dropped`);
      return;
    }
    // conversations.open → chat.postMessage. Slack accepts the user id for
    // chat.postMessage directly and will open/reuse the IM channel as long
    // as the bot has im:write.
    // CR #3108398187: 5-second abort on conversations.open matches the
    // timeout used by sendSlackChannelMessage; without it a stalled Slack
    // API call would tie up a scheduler slot indefinitely.
    try {
      const controller = new AbortController();
      const timeoutHandle = setTimeout(() => controller.abort(), 5_000);
      let openJson: { ok: boolean; error?: string; channel?: { id: string } };
      try {
        const openResp = await fetch('https://slack.com/api/conversations.open', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${botToken}`,
            'Content-Type': 'application/json; charset=utf-8',
          },
          body: JSON.stringify({ users: resolved.slack_user_id }),
          signal: controller.signal,
        });
        openJson = (await openResp.json()) as { ok: boolean; error?: string; channel?: { id: string } };
      } finally {
        clearTimeout(timeoutHandle);
      }
      if (!openJson.ok || !openJson.channel?.id) {
        const errCode = openJson.error === 'missing_scope' ? 'SLACK_MISSING_SCOPE' : `SLACK_OPEN_FAILED:${openJson.error ?? 'unknown'}`;
        log(`[delivery] conversations.open failed for '${agentCodeName}': ${openJson.error}`);
        await reportDeliveryStatus(agentId, taskId, { status: 'failed', error_code: errCode, medium: 'slack' });
        return;
      }
      const sent = await postSlackChannelMessage(agentCodeName, openJson.channel.id, footeredBody);
      await reportDeliveryStatus(agentId, taskId, {
        status: sent.ok ? 'ok' : 'failed',
        medium: 'slack',
        error_code: sent.ok ? null : 'SLACK_SEND_FAILED',
      });
      if (sent.ok) await maybePostSlackThreadHint(agentCodeName, openJson.channel.id, sent.ts);
    } catch (err) {
      const isAbort = (err as Error).name === 'AbortError';
      const errCode = isAbort ? 'SLACK_OPEN_TIMEOUT' : 'SLACK_EXCEPTION';
      log(`[delivery] Slack DM ${isAbort ? 'timeout' : 'failure'} for '${agentCodeName}': ${(err as Error).message}`);
      await reportDeliveryStatus(agentId, taskId, { status: 'failed', error_code: errCode, medium: 'slack' });
    }
    return;
  }

  if (resolved.kind === 'dm' && resolved.medium === 'telegram') {
    const tokens = agentChannelTokens.get(agentCodeName);
    const botToken = tokens?.telegram;
    if (!botToken) {
      log(`[delivery] No Telegram bot token for '${agentCodeName}' — DM dropped`);
      await reportDeliveryStatus(agentId, taskId, { status: 'failed', error_code: 'TELEGRAM_NO_TOKEN', medium: 'telegram' });
      return;
    }
    try {
      const result = await telegramApiCall(botToken, 'sendMessage', {
        chat_id: resolved.telegram_chat_id,
        text: footeredBody,
      });
      if (!result.ok) {
        log(`[delivery] Telegram DM failed for '${agentCodeName}': ${result.description}`);
        await reportDeliveryStatus(agentId, taskId, { status: 'failed', error_code: `TELEGRAM_SEND_FAILED:${result.description ?? 'unknown'}`, medium: 'telegram' });
        return;
      }
      await reportDeliveryStatus(agentId, taskId, { status: 'ok', medium: 'telegram' });
      await maybeSendTelegramFollowUpHint(agentCodeName, botToken, resolved.telegram_chat_id);
    } catch (err) {
      log(`[delivery] Telegram DM exception for '${agentCodeName}': ${(err as Error).message}`);
      await reportDeliveryStatus(agentId, taskId, { status: 'failed', error_code: 'TELEGRAM_EXCEPTION', medium: 'telegram' });
    }
  }
}
